import { useEffect, useState, useMemo } from "react";
import { GasOrder } from "../../types/gasOrder.types";
import { getPendingPayments, processManualPayment } from "../../services/pendingPaymentsService";
import { useUserStationParam } from "../../hooks/useUserStationParam";
import { FormControl, InputLabel, MenuItem, Select, IconButton, Tooltip } from "@mui/material";
import { Sync as SyncIcon } from "@mui/icons-material";
import {
  PageContainer,
  PageHeader,
  PageTitle,
} from "../../components/PageLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { toast } from "react-toastify";
import { formatIsoToBrDateTimeShort } from "../../utils/functions";

export default function PendingPayments() {
  const { stationId: selectedStationId, resolving: resolvingStation } = useUserStationParam();
  const [selectedGasPump, setSelectedGasPump] = useState<string>("all");
  const [allOrders, setAllOrders] = useState<GasOrder[]>([]);
  const [loading, setLoading] = useState(false);

  const loadOrders = async (silent = false) => {
    if (!selectedStationId) return;
    if (!silent) setLoading(true);
    try {
      const data = await getPendingPayments(selectedStationId);
      setAllOrders(data);
    } catch {
      if (!silent) toast.error("Erro ao carregar pagamentos pendentes.");
      setAllOrders((prev) => (silent ? prev : []));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedGasPump("all");
    loadOrders();
    if (!selectedStationId) return;
    const intervalId = setInterval(() => loadOrders(true), 30 * 1000);
    return () => clearInterval(intervalId);
  }, [selectedStationId]);

  const availablePumps = useMemo(() => {
    const pumps = allOrders.map((o) => o.gasPump);
    return Array.from(new Set(pumps)).sort((a, b) => Number(a) - Number(b));
  }, [allOrders]);

  const orders = useMemo(() => {
    if (!selectedGasPump || selectedGasPump === "all") return allOrders;
    return allOrders.filter((order) => order.gasPump === selectedGasPump);
  }, [allOrders, selectedGasPump]);

  const handleManualPayment = async (id: string) => {
    try {
      const responseMessage = await processManualPayment(id);
      toast.success(typeof responseMessage === 'string' ? responseMessage : "Pagamento processado com sucesso");
      loadOrders(true); // Recarrega tela
    } catch (e) {
      toast.error("Erro ao processar pagamento");
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Pagamentos pendentes (Combustível)</PageTitle>
        <div style={{ display: 'flex', gap: '16px' }}>
          {selectedStationId && availablePumps.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150, backgroundColor: "#fff" }}>
              <InputLabel id="pump-select-label">Bomba</InputLabel>
              <Select
                labelId="pump-select-label"
                value={selectedGasPump}
                label="Bomba"
                onChange={(e) => setSelectedGasPump(e.target.value)}
              >
                <MenuItem value="all">Todas</MenuItem>
                {availablePumps.map((pump) => (
                  <MenuItem key={pump} value={pump}>
                    Bomba {pump}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
      </PageHeader>

      {resolvingStation ? (
        <p>Carregando...</p>
      ) : !selectedStationId ? (
        <p>Nenhum posto vinculado ao usuário.</p>
      ) : loading && allOrders.length === 0 ? (
        <p>Carregando...</p>
      ) : orders.length === 0 ? (
        <p>Nenhum pagamento pendente encontrado para este posto.</p>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: "auto", marginTop: "16px" }}>
          <Table sx={{ minWidth: 650 }} aria-label="pending payments table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Bomba / Tipo</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Cliente</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Data da transação</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="right">Quantidade</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="right">Valor</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
                    Bomba {order.gasPump} · {order.type}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {order.user?.full_name || order.user?.email || "—"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {order.updatedAt
                      ? formatIsoToBrDateTimeShort(order.updatedAt)
                      : "—"}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }} align="right">
                    {order.quantity.toFixed(2)} L
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }} align="right">
                    R$ {order.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }} align="center">
                    <Tooltip title="Processar pagamento manual PIX">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleManualPayment(order.id)}
                      >
                        <SyncIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </PageContainer>
  );
}
